import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { LockKey } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('A senha deve ter no mínimo 6 caracteres.'); return; }
    if (password !== confirm) { setError('As senhas não coincidem.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      toast.success('Senha redefinida com sucesso! Faça login com a nova senha.');
      navigate('/login');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Erro ao redefinir senha. Solicite um novo link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold tracking-tight text-zinc-950">SOP & RAFT</h1>
          <p className="text-[10px] tracking-[0.15em] uppercase text-zinc-400 mt-1 font-semibold">Cozinha Inteligente</p>
        </div>
        <div className="bg-white rounded-sm border border-zinc-200 shadow-sm p-8">
          <div className="flex items-center gap-2 mb-1">
            <LockKey size={20} className="text-zinc-700" weight="bold" />
            <h2 className="text-xl font-bold text-zinc-950">Redefinir senha</h2>
          </div>
          <p className="text-sm text-zinc-500 mb-6">Crie uma nova senha para sua conta.</p>
          {!token ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700" data-testid="reset-no-token">
              Link inválido. Solicite um novo em <Link to="/forgot-password" className="font-semibold underline">Esqueci minha senha</Link>.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-sm" data-testid="reset-password-error">
                  <p className="text-sm text-red-700 font-medium">{error}</p>
                </div>
              )}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Nova senha</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  required placeholder="Mínimo 6 caracteres" className="mt-1.5" data-testid="reset-password-new" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Confirmar senha</Label>
                <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  required placeholder="Repita a senha" className="mt-1.5" data-testid="reset-password-confirm" />
              </div>
              <Button type="submit" className="w-full bg-zinc-950 text-white hover:bg-zinc-800" disabled={loading} data-testid="reset-password-submit">
                {loading ? 'Salvando...' : 'Redefinir senha'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
